import React, { useState } from "react";
import { Box, Typography, Button, TextField, Switch, FormControlLabel, Modal, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { fetchCreateOptionType, fetchCreateOptionValue, fetchOptionTypes } from "../../store/reducers/createProfile/apithunk";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";

const AddNewVariantTypeModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [variantTypeName, setVariantTypeName] = useState("");
  const [variantOptions, setVariantOptions] = useState([{ value: "", color: "" }]);
  const [isColorVariant, setIsColorVariant] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddMoreOption = () => {
    setVariantOptions([...variantOptions, { value: "", color: "" }]);
  };

  const handleOptionChange = (index, field, newValue) => {
    const updated = [...variantOptions];
    updated[index] = { ...updated[index], [field]: newValue };
    setVariantOptions(updated);
  };

  const handleRemoveOption = (index) => {
    if (variantOptions?.length > 1) {
      setVariantOptions(variantOptions?.filter((_, i) => i !== index));
    }
  };

  const handleAdd = async () => {
    // Validation
    if (!variantTypeName.trim()) {
      ToastNotifyError("Variant Type is required");
      return;
    }

    const validOptions = variantOptions?.filter(opt => opt.value.trim());
    if (validOptions?.length === 0) {
      ToastNotifyError("At least one variant option is required");
      return;
    }

    setIsLoading(true);
    try {
      // Step 1: Create option type
      const optionTypePayload = {
        name: variantTypeName.trim(),
        display_name: variantTypeName.trim(),
        description: "",
        is_active: true,
        sort_order: 1
      };

      const optionTypeResponse = await dispatch(fetchCreateOptionType(optionTypePayload));
      const { status, data, message } = optionTypeResponse?.payload || {};

      if (!status || !data) {
        if (optionTypeResponse?.payload?.status !== 400) {
        ToastNotifyError(message || "Failed to create variant type");
        } else {
          ToastNotifyError(optionTypeResponse?.payload?.response?.data?.message || "Failed to create variant type");
        }
        setIsLoading(false);
        return;
      }

      const optionTypeId = data?.id || data?.data?.id;
      if (!optionTypeId) {
        ToastNotifyError("Failed to get option type ID");
        setIsLoading(false);
        return;
      }

      // Step 2: Create option values
      const valuePromises = validOptions?.map((option, index) => {
        const payload = {
          option_type: optionTypeId,
          value: option.value.trim(),
          position: index + 1,
          is_active: true,
          hex_code: isColorVariant && option.color ? option.color : null,
          metadata: {}
        };
        return dispatch(fetchCreateOptionValue(payload));
      });

      const valueResponses = await Promise.all(valuePromises);
      const allSuccess = valueResponses?.every(res => {
        const { status } = res?.payload || {};
        return status;
      });

      if (!allSuccess) {
        ToastNotifyError("Some option values failed to create");
        setIsLoading(false);
        return;
      }

      // Step 3: Refresh option types list
      const refreshResponse = await dispatch(fetchOptionTypes());
      const refreshData = refreshResponse?.payload?.data || [];
      
      ToastNotifySuccess("Variant type created successfully!");
      
      // Reset form
      setVariantTypeName("");
      setVariantOptions([{ value: "", color: "" }]);
      setIsColorVariant(false);
      
      // Close modal and notify parent with new option type
      onClose();
      if (onSuccess) {
        // Find the newly created option type
        const newOptionType = refreshData?.find(opt => opt.id === optionTypeId);
        onSuccess(newOptionType);
      }
    } catch (error) {
      rollbar.error("Error creating variant type", error);
      ToastNotifyError("Failed to create variant type");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setVariantTypeName("");
      setVariantOptions([{ value: "", color: "" }]);
      setIsColorVariant(false);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        // Only close on Cancel button click, not on backdrop click
        if (reason === 'backdropClick') {
          return;
        }
        handleClose();
      }}
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          p: 3,
          maxWidth: '500px',
          width: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <Typography sx={{
          fontFamily: 'Lexend',
          fontSize: '20px',
          fontWeight: 500,
          mb: 3,
          color: theme.palette?.primary?.main || '#0F0F0F',
        }}>
          Add New Variant Type
        </Typography>

        {/* Variant Type Input */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{
            fontFamily: 'Lexend',
            fontSize: '14px',
            fontWeight: 400,
            mb: 1,
            color: theme.palette?.primary?.main || '#0F0F0F',
          }}>
            Variant Type<span style={{ color: '#D32F2F' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter Variant Name"
            value={variantTypeName}
            onChange={(e) => setVariantTypeName(e.target.value)}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Lexend',
                fontSize: '14px',
              }
            }}
          />
        </Box>

        {/* Variant Options */}
        {variantOptions?.map((option, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              mb: 1,
              color: theme.palette?.primary?.main || '#0F0F0F',
            }}>
              Variant Option {index + 1}<span style={{ color: '#D32F2F' }}>*</span>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {isColorVariant && (
                <Box
                  component="input"
                  type="color"
                  value={option.color || '#000000'}
                  onChange={(e) => handleOptionChange(index, 'color', e.target.value)}
                  disabled={isLoading}
                  sx={{
                    width: '50px',
                    height: '40px',
                    border: `1px solid ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
              )}
              <TextField
                fullWidth
                placeholder="Add Variant Option Name"
                value={option.value}
                onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'Lexend',
                    fontSize: '14px',
                  }
                }}
              />
              {variantOptions?.length > 1 && (
                <IconButton
                  onClick={() => handleRemoveOption(index)}
                  disabled={isLoading}
                  sx={{
                    color: theme.palette?.customColor?.red || '#D32F2F',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    }
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        {/* Add More Option Button */}
        <Button
          onClick={handleAddMoreOption}
          disabled={isLoading}
          sx={{
            fontFamily: 'Lexend',
            fontSize: '14px',
            fontWeight: 400,
            color: theme.palette?.primary?.main || '#0F0F0F',
            textTransform: 'none',
            padding: 0,
            minWidth: 'auto',
            mb: 3,
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            }
          }}
        >
          +Add More Option
        </Button>

        {/* Is Color Variant Toggle */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isColorVariant}
                onChange={(e) => setIsColorVariant(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Typography sx={{
                fontFamily: 'Lexend',
                fontSize: '14px',
                fontWeight: 400,
                color: theme.palette?.primary?.main || '#0F0F0F',
              }}>
                Is this color variant
              </Typography>
            }
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              borderColor: theme.palette?.customColor?.secondaryThird || "#E0E0E0",
              color: theme.palette?.primary?.main || '#0F0F0F',
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '4px',
              px: 3,
              py: 1,
              '&:hover': {
                borderColor: theme.palette?.primary?.main || '#0F0F0F',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={isLoading}
            sx={{
              backgroundColor: theme.palette?.primary?.main || '#0F0F0F',
              color: '#FFFFFF',
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '4px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.palette?.primary?.dark || '#000000',
              },
              '&:disabled': {
                backgroundColor: theme.palette?.action?.disabled || '#E0E0E0',
              }
            }}
          >
            {isLoading ? "Adding..." : "Add"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddNewVariantTypeModal;

